import CloseIcon from "@mui/icons-material/CloseRounded";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineRounded";
import { Badge, Box, Drawer, IconButton, Tooltip, Typography } from "@mui/material";
import { green } from "@mui/material/colors";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer";
import { useIntercomContext } from "@entrilia/shared/contexts/IntercomContext";
import { makeModeAwareAccentSurface } from "@entrilia/shared/utilities/colorHelper";
import type { ReportConfiguration, ReportType } from "../../../../../../api/types/svTypes";
import { selectFieldsStateConfigurationSafe } from "../../store/selectors/fieldsStateSelectors";
import { BiAgentChatContainer } from "./BiAgentChatContainer";
import { useBiAgentChat } from "./useBiAgentChat";

interface BiAgentPanelProps {
  open: boolean;
  currentReportType?: ReportType;
  currentDataSetId?: string;
  onClose: () => void;
  onApplyConfiguration?: (configuration: ReportConfiguration, reportType?: ReportType, dataSetId?: string) => void;
}

const DRAWER_WIDTH = 480;

export const BiAgentPanel = ({
  open,
  onClose,
  currentReportType,
  currentDataSetId,
  onApplyConfiguration,
}: BiAgentPanelProps) => {
  const currentConfiguration = useSelector(selectFieldsStateConfigurationSafe);
  const {
    messages,
    isLoading,
    statusMessage,
    appliedMessages,
    sendMessage,
    selectOption,
    applyConfiguration,
    revertConfiguration,
    clearChat,
  } = useBiAgentChat({
    currentConfiguration,
    currentReportType,
    currentDataSetId,
    onApplyConfiguration,
  });

  const { addEffect, removeEffect } = useIntercomContext();
  useEffect(() => {
    if (!open) return;
    addEffect("hide");
    return () => removeEffect("hide");
  }, [open, addEffect, removeEffect]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="persistent"
      sx={{
        width: open ? DRAWER_WIDTH : 0,

        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: open ? DRAWER_WIDTH : 0,
          boxSizing: "border-box",
          position: "relative",
          borderLeftWidth: open ? 1 : 0,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 1,
          px: 2.5,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Badge
          badgeContent={
            <Tooltip title="Beta — actively improving. Feedback welcome." arrow placement="top">
              <Box component="span" sx={{ pointerEvents: "auto", cursor: "default" }}>
                Beta
              </Box>
            </Tooltip>
          }
          sx={(t) => ({
            pr: 2.5,
            "& .MuiBadge-badge": {
              ...makeModeAwareAccentSurface(t, green),
              fontWeight: 500,
              pointerEvents: "none",
            },
          })}
        >
          <Typography id="bi-agent-title" variant="h6">
            SmartViews Assistant
          </Typography>
        </Badge>
        <Box>
          <IconButton size="small" onClick={clearChat} title="Clear chat">
            <DeleteOutlineIcon />
          </IconButton>
          <IconButton size="small" onClick={onClose} title="Close">
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>
      <ScrollableFlexContainer sx={{ flex: 1, overflow: "hidden" }}>
        <BiAgentChatContainer
          messages={messages}
          isLoading={isLoading}
          statusMessage={statusMessage}
          appliedMessages={appliedMessages}
          onSendMessage={sendMessage}
          onSelectOption={selectOption}
          onApplyConfiguration={applyConfiguration}
          onRevertConfiguration={revertConfiguration}
        />
      </ScrollableFlexContainer>
    </Drawer>
  );
};
